import api from "../../../lib/axios";
import { apiUtils } from "../../../utils/apiUtils";
import {
  getPublicCategoryOptions,
  getPublicNeighborhoodOptions,
} from "../../common/public-options";
import type { AreaModel } from "../types/Area";
import type { AreaAnalysisModel } from "../types/AreaAnalysis";
import type { OptionModel } from "../types/Filter";
import type { LiveEventAreaModel } from "../types/LiveEvent";
import type { MerchantModel } from "../types/Merchant";
import type { HeatMapStatisticRequest, StatisticModel } from "../types/Statistic";
import type { TargetOpportunityModel } from "../types/TargetOppotunity";
import type { DashboardFilterPayload } from "../utils/heat-map.utils";

export interface ExportHeatMapReportResult {
  data: BlobPart;
  contentType: string;
  filename: string;
}

const toOptionModel = (options: { value: string; label: string }[]): OptionModel[] =>
  options.map((option) => ({ key: option.value, value: option.label }));

export function getHeatMapStatistics(
  params: HeatMapStatisticRequest,
  signal?: AbortSignal,
): Promise<StatisticModel> {
  return apiUtils.getData<StatisticModel>(
    "/v1/portal/dashboard/statistic",
    "Failed to fetch dashboard statistics",
    { params, signal },
  );
}

export function getTargetOpportunities(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<TargetOpportunityModel[]> {
  return apiUtils.postData<TargetOpportunityModel[]>(
    "/v1/portal/heat-map/target-opportunities",
    payload,
    "Failed to fetch target opportunities",
    { signal },
  );
}

export function getAreaAnalysis(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<AreaAnalysisModel[]> {
  return apiUtils.postData<AreaAnalysisModel[]>(
    "/v1/portal/heat-map/area-analysis",
    payload,
    "Failed to fetch area analysis",
    { signal },
  );
}

export function getHeatMapAreas(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<AreaModel[]> {
  return apiUtils.postData<AreaModel[]>(
    "/v1/portal/heat-map/heatmap",
    payload,
    "Failed to fetch heat map",
    { signal },
  );
}

export function getLiveEventsMap(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<LiveEventAreaModel[]> {
  return apiUtils.postData<LiveEventAreaModel[]>(
    "/v1/portal/heat-map/live-events-map",
    payload,
    "Failed to fetch live events",
    { signal },
  );
}

export function toMerchantsMapQuery(payload: DashboardFilterPayload): string {
  const params = new URLSearchParams();
  const filter = payload.filter ?? {};

  for (const categoryId of filter.category_ids ?? []) {
    if (categoryId.trim() !== "") params.append("category_ids", categoryId);
  }
  if (filter.merchant_name?.trim()) {
    params.append("merchant_name", filter.merchant_name.trim());
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function getHeatMapMerchants(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<MerchantModel[]> {
  return apiUtils.getData<MerchantModel[]>(
    `/v1/portal/heat-map/merchants-map${toMerchantsMapQuery(payload)}`,
    "Failed to fetch merchants",
    { signal },
  );
}

export async function exportHeatMapReport(
  payload: DashboardFilterPayload,
  signal?: AbortSignal,
): Promise<ExportHeatMapReportResult> {
  const response = await api.post("/v1/portal/heat-map/export-general-heatmap-report", payload, {
    responseType: "blob",
    signal,
  });

  const contentDisposition: string | undefined = response.headers?.["content-disposition"] as
    string | undefined;
  const filename =
    contentDisposition?.split("filename=")[1]?.replace(/"/g, "") || "GeneralHeatmapReport.xlsx";

  return {
    data: response.data as BlobPart,
    contentType:
      (response.headers?.["content-type"] as string | undefined) ?? "application/octet-stream",
    filename,
  };
}

export async function getHeatMapNeighborhoodOptions(organizationId: string): Promise<OptionModel[]> {
  return toOptionModel(await getPublicNeighborhoodOptions(organizationId));
}

export async function getHeatMapCategoryOptions(): Promise<OptionModel[]> {
  return toOptionModel(await getPublicCategoryOptions());
}
